import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaEdit, FaTrashAlt } from "react-icons/fa";
import { deleteOfficeService } from "./supportContactService";
import { SupportSortableHeader } from "./SupportSortableHeader";
import { useResizableColumns } from "../hooks/useResizableColumns";
import "../assets/css/SupportContacts.css";
import "../assets/css/DigixLeads.css";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { getAuthItem } from "../utils/authStorage";
import { useConfirm } from "../components/ConfirmModal";

const SERVICE_COLUMN_WIDTHS = [160, 120, 140, 130, 160, 90, 120, 120, 100];

const SUPPORT_STATUSES = ["Active", "Inactive"];

/**
 * SupportService
 *
 * Page component for the Service tab in the Support module.
 * Displays service support contacts with filters, table, and pagination.
 */
function SupportService({
  serviceContacts = [],
  onRefresh,
  onAddContact,
  onViewContact,
  onEditContact,
  onDeleteContact,
  categories = [],
  rowsPerPage = 5
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const role = (() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  })();
  const canAdd = canPerformActionOnPage(PAGES.SUPPORT_CONTACTS, ACTIONS.ADD, role);
  const canEdit = canPerformActionOnPage(PAGES.SUPPORT_CONTACTS, ACTIONS.EDIT, role);
  const canDelete = canPerformActionOnPage(PAGES.SUPPORT_CONTACTS, ACTIONS.DELETE, role);
  const [filters, setFilters] = useState({
    name: "",
    category: "",
    status: "All Statuses",
    freeSearch: ""
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const skipPageResetOnFiltersChangeRef = useRef(true);
  const [deletingId, setDeletingId] = useState(null);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const categoryDropdownRef = useRef(null);
  const { getColumnStyle, handleResizeStart } = useResizableColumns(SERVICE_COLUMN_WIDTHS);

  useEffect(() => {
    if (skipPageResetOnFiltersChangeRef.current) {
      skipPageResetOnFiltersChangeRef.current = false;
    } else {
      setCurrentPage(1);
    }
  }, [filters]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
        setCategorySearchQuery("");
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    const empty = { name: "", category: "", status: "All Statuses", freeSearch: "" };
    setFilters(empty);
    setSortColumn(null);
    setSortDirection("asc");
    setCategoryDropdownOpen(false);
    setCategorySearchQuery("");
    setCurrentPage(1);
  };

  const handleDeleteContact = async (contactId) => {
    if (!(await confirm({ title: "Delete Service", message: "Are you sure you want to delete this service?" }))) return;
    setDeletingId(contactId);
    try {
      await deleteOfficeService(contactId);
      if (onRefresh) onRefresh();
      if (onDeleteContact) onDeleteContact(contactId);
    } catch (err) {
      console.error("Error deleting service:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to delete service.");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredContacts = useMemo(() => {
    return serviceContacts.filter((c) => {
      const name = (c.Service_Name ?? c.service_name ?? c.name ?? "").toLowerCase();
      const category = (c.Service_Category ?? c.service_category ?? c.Category ?? c.category ?? "").toLowerCase();
      const status = (c.Status ?? c.status ?? "").toLowerCase();
      const primaryContact = (c.Contact_Name ?? c.contact_name ?? c.Primary_Contact_Name ?? c.primary_contact_name ?? "").toLowerCase();
      const phone = (c.Contact_Phone ?? c.contact_phone ?? c.Primary_Contact_Phone ?? c.Alt_Phone ?? c.Support_Phone ?? c.support_phone ?? "").toString();
      const email = (c.Support_Email ?? c.support_email ?? "").toLowerCase();
      const supportDays = (c.Support_Days ?? c.support_days ?? "").toLowerCase();
      const f = filters;

      if (f.name && !name.includes(f.name.toLowerCase())) return false;
      if (f.category && !category.includes(f.category.toLowerCase())) return false;
      if (f.status && f.status !== "All Statuses") {
        if (status !== f.status.toLowerCase()) return false;
      }
      if (f.freeSearch) {
        const search = f.freeSearch.toLowerCase().trim();
        if (!search) return true;
        const matches =
          name.includes(search) ||
          category.includes(search) ||
          status.includes(search) ||
          primaryContact.includes(search) ||
          phone.includes(search) ||
          email.includes(search) ||
          supportDays.includes(search);
        if (!matches) return false;
      }
      return true;
    });
  }, [serviceContacts, filters]);

  const displayedContacts = useMemo(() => {
    if (!sortColumn) return filteredContacts;
    const val = (c, key) => {
      switch (key) {
        case "serviceName":
          return String(c.Service_Name ?? c.service_name ?? c.name ?? "").toLowerCase();
        case "category":
          return String(c.Service_Category ?? c.service_category ?? c.Category ?? c.category ?? "").toLowerCase();
        case "contactName":
          return String(
            c.Contact_Name ?? c.contact_name ?? c.Primary_Contact_Name ?? c.primary_contact_name ?? ""
          ).toLowerCase();
        case "phone":
          return String(
            c.Contact_Phone ??
              c.contact_phone ??
              c.Primary_Contact_Phone ??
              c.Alt_Phone ??
              c.Support_Phone ??
              c.support_phone ??
              ""
          ).toLowerCase();
        case "email":
          return String(c.Support_Email ?? c.support_email ?? "").toLowerCase();
        case "status":
          return String(c.Status ?? c.status ?? "").toLowerCase();
        case "supportHours":
          return String(c.Support_Hours ?? c.support_hours ?? "").toLowerCase();
        case "supportDays":
          return String(c.Support_Days ?? c.support_days ?? "").toLowerCase();
        default:
          return "";
      }
    };
    const mult = sortDirection === "desc" ? -1 : 1;
    const rowId = (c) =>
      Number(c.Id ?? c.Support_Contact_Id ?? c.support_contact_id ?? c.id ?? 0);
    return [...filteredContacts].sort((a, b) => {
      const va = val(a, sortColumn);
      const vb = val(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return (rowId(a) - rowId(b)) * mult;
    });
  }, [filteredContacts, sortColumn, sortDirection]);

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const totalPages = Math.ceil(displayedContacts.length / rowsPerPage) || 1;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageData = displayedContacts.slice(startIndex, endIndex);
  const showPagination = displayedContacts.length > rowsPerPage;

  const handlePrevPage = () => setCurrentPage((p) => (p > 1 ? p - 1 : p));
  const handleNextPage = () => setCurrentPage((p) => (p < totalPages ? p + 1 : p));
  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, "...", totalPages];
    if (currentPage >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  };

  const handleAddClick = () => {
    if (onAddContact) onAddContact("Service");
    else navigate("/support-contacts/add?type=service");
  };

  const handleViewClick = (id) => {
    if (onViewContact) onViewContact(id);
    else navigate(`/support-contacts/${id}/view`);
  };

  const handleEditClick = (id) => {
    if (onEditContact) onEditContact(id);
    else navigate(`/support-contacts/${id}/edit`);
  };

  return (
    <div className="support-page-content">
      <div className="search-actions-row">
        <div className="search-filters-row">
          <input
            type="text"
            className="search-filter-input"
            placeholder="Search by name..."
            value={filters.name}
            onChange={(e) => handleFilterChange("name", e.target.value)}
          />
          <div className="domain-filter-dropdown" ref={categoryDropdownRef}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              onClick={() => setCategoryDropdownOpen((o) => !o)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setCategoryDropdownOpen((o) => !o);
                }
              }}
            >
              <span>{filters.category || "All Categories"}</span>
              <FaChevronDown className={`domain-filter-chevron ${categoryDropdownOpen ? "open" : ""}`} />
            </div>
            {categoryDropdownOpen && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search categories..."
                  value={categorySearchQuery}
                  onChange={(e) => setCategorySearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!filters.category ? "selected" : ""}`}
                    onClick={() => {
                      handleFilterChange("category", "");
                      setCategoryDropdownOpen(false);
                      setCategorySearchQuery("");
                    }}
                  >
                    All Categories
                  </div>
                  {(() => {
                    const filteredCategories = (categories || []).filter(
                      (cat) =>
                        !categorySearchQuery.trim() ||
                        String(cat || "").toLowerCase().includes(categorySearchQuery.trim().toLowerCase())
                    );
                    return filteredCategories.length > 0 ? (
                      filteredCategories.map((cat) => (
                        <div
                          key={cat}
                          className={`domain-filter-option ${filters.category === cat ? "selected" : ""}`}
                          onClick={() => {
                            handleFilterChange("category", cat);
                            setCategoryDropdownOpen(false);
                            setCategorySearchQuery("");
                          }}
                        >
                          {cat}
                        </div>
                      ))
                    ) : (
                      <div className="domain-filter-no-results">No categories found</div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <select
            className="search-filter-input search-filter-select"
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option value="">All Statuses</option>
            {SUPPORT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button type="button" className="btn-clear" onClick={handleClearFilters} style={{ marginLeft: 8 }}>
            Clear
          </button>
        </div>
        <div className="header-actions">
          {canAdd && (
            <button type="button" className="btn-primary btn-add-lead" onClick={handleAddClick}>
              Add Service
            </button>
          )}
        </div>
      </div>

      <div className="digixleads-content">
        <div className="search-row-2">
          <div className="free-search-bar search-in-actions-row">
            <input
              type="text"
              className="free-search-input"
              placeholder="Search..."
              value={filters.freeSearch}
              onChange={(e) => handleFilterChange("freeSearch", e.target.value)}
              title="Search across all columns"
            />
          </div>
        </div>

        <div className="table-wrapper">
          <div className="table-container">
            <table className="data-table leads-table-frozen support-table-resizable">
              <thead>
                <tr>
                  <th className="col-sticky col-0 support-th-sortable" style={getColumnStyle(0)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="serviceName"
                        label="Service Name"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(0, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(1)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="category"
                        label="Category"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(1, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(2)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="contactName"
                        label="Contact Name"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(2, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(3)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="phone"
                        label="Phone"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(3, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(4)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="email"
                        label="Email"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(4, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(5)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="status"
                        label="Status"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(5, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(6)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="supportHours"
                        label="Support Hours"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(6, e)} aria-hidden />
                  </th>
                  <th className="support-th-sortable" style={getColumnStyle(7)}>
                    <div className="support-th-sort-row">
                      <SupportSortableHeader
                        columnId="supportDays"
                        label="Support Days"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </div>
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(7, e)} aria-hidden />
                  </th>
                  <th className="col-sticky col-action" style={getColumnStyle(8)}>
                    Actions
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeStart(8, e)} aria-hidden />
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageData.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="supportcontacts-empty-cell">
                      No service contacts yet.
                    </td>
                  </tr>
                ) : (
                  pageData.map((contact) => {
                    const id = contact.Id ?? contact.Support_Contact_Id ?? contact.support_contact_id ?? contact.id;
                    const serviceName = contact.Service_Name ?? contact.service_name ?? contact.name ?? "—";
                    const category = contact.Service_Category ?? contact.service_category ?? contact.Category ?? contact.category ?? "—";
                    const primaryContact = contact.Contact_Name ?? contact.contact_name ?? contact.Primary_Contact_Name ?? contact.primary_contact_name ?? "—";
                    const phone = contact.Contact_Phone ?? contact.contact_phone ?? contact.Primary_Contact_Phone ?? contact.Alt_Phone ?? contact.Support_Phone ?? contact.support_phone ?? "—";
                    const email = contact.Support_Email ?? contact.support_email ?? "—";
                    const status = contact.Status ?? contact.status ?? "—";
                    const supportHours = contact.Support_Hours ?? contact.support_hours ?? "—";
                    const supportDays = contact.Support_Days ?? contact.support_days ?? "—";

                    return (
                      <tr key={id}>
                        <td className="col-sticky col-0">
                          <span
                            className="table-name-link"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleViewClick(id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleViewClick(id);
                              }
                            }}
                          >
                            <strong>{serviceName}</strong>
                          </span>
                        </td>
                        <td>{category}</td>
                        <td>{primaryContact}</td>
                        <td>{phone}</td>
                        <td>{email}</td>
                        <td>
                          <span className={`status-badge status-${(status || "").toLowerCase()}`}>
                            {status || "—"}
                          </span>
                        </td>
                        <td>{supportHours}</td>
                        <td>{supportDays}</td>
                        <td className="col-sticky col-action">
                          <div className="project-actions-cell">
                            {canEdit && (
                              <button
                                type="button"
                                className="project-action-btn project-action-btn-icon project-action-btn-edit"
                                title="Edit contact"
                                onClick={() => handleEditClick(id)}
                              >
                                <FaEdit />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                                title="Delete contact"
                                disabled={deletingId === id}
                                onClick={() => handleDeleteContact(id)}
                              >
                                <FaTrashAlt />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {showPagination && (
            <div className="pagination">
              <button
                className="pagination-btn pagination-arrow"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <FaChevronLeft />
              </button>
              <div className="page-numbers">
                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span key={`ellipsis-${index}`} className="page-number ellipsis">
                      ...
                    </span>
                  ) : (
                    <span
                      key={page}
                      className={`page-number ${currentPage === page ? "active" : ""}`}
                      onClick={() => page !== "..." && setCurrentPage(page)}
                    >
                      {page}
                    </span>
                  )
                )}
              </div>
              <button
                className="pagination-btn pagination-arrow"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                <FaChevronRight />
              </button>
              <div className="pagination-info">
                {startIndex + 1}-{Math.min(endIndex, displayedContacts.length)} of {displayedContacts.length} items
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupportService;
